import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import CaseStudy from '../components/CaseStudy';
import CallToAction from '../components/CallToAction';
import Badge from '../components/ui/Badge';
import usePageMeta from '../hooks/usePageMeta';
import pages from '../content/pages.json';
import { projects } from '../data/projects';
import Section from '../components/ui/Section';
import Container from '../components/ui/Container';
import { posts } from '../data/blog';

const Index = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const IndexLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 48px;
  padding: 0.4rem 0.6rem 0.4rem 1rem;
  border-radius: var(--radius-pill);
  border: 1px solid var(--border-strong);
  background: var(--surface);
  font-weight: 600;
  font-size: 0.95rem;
  transition: background-color 0.18s ease, border-color 0.18s ease;

  &:hover {
    background: var(--surface-hover);
    border-color: rgba(255, 255, 255, 0.28);
  }
`;

const ProcessNote = styled.p`
  color: var(--text-2);
  font-size: 1.0625rem;
  max-width: 62ch;

  a {
    color: var(--text-1);
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const PROCESS_POST = 'how-we-build-a-local-business-website';

const Projects: React.FC = () => {
  usePageMeta(pages.projects);
  const processPost = posts.find((post) => post.slug === PROCESS_POST);

  return (
    <Layout>
      <PageHero
        id="top"
        eyebrow="Work"
        title="Our work for Puerto Rico businesses."
        lead="Three recent projects: a production caregiving platform we built and still maintain, an appointment-first site for a local auto detailing studio, and a bilingual brand site for a pet-care business launching soon."
      >
        <nav aria-label="Case studies">
          <Index>
            {projects.map((project) => (
              <li key={project.slug}>
                <IndexLink to={`/projects#${project.slug}`}>
                  {project.name}
                  <Badge variant={project.status} />
                </IndexLink>
              </li>
            ))}
          </Index>
        </nav>
      </PageHero>

      {projects.map((project, index) => (
        <CaseStudy key={project.slug} project={project} index={index} tone={index % 2 === 0 ? 'raised' : 'base'} />
      ))}

      {processPost && (
        <Section aria-label="How we build">
          <Container>
            <ProcessNote>
              Curious how a project like these goes from first call to launch? Read{' '}
              <Link to={`/blog/${processPost.slug}`}>
                {processPost.title.charAt(0).toLowerCase() + processPost.title.slice(1)}
              </Link>{' '}
              on our blog.
            </ProcessNote>
          </Container>
        </Section>
      )}

      <CallToAction title="Have a similar project?" />
    </Layout>
  );
};

export default Projects;
